/* ---------------- Helpers: default + mapping + formatting ---------------- */

import { ClipboardList, HospitalIcon, ScrollText, TreePalm, User, DollarSign } from "lucide-react";

export const getDefaultEpfForm = () => ({
    epf_number: "",
    deduction_cycle: "monthly",
    employee_contribution_rate: "12_actual",
    employer_contribution_rate: "12_actual",
    include_employer_contribution: true,
    include_edli: false,
    include_admin_charges: false,
    override_pf: false,
    pro_rate_pf: false,
    consider_applicable_if_less_than_15000: true,
});

export const getDefaultEsiForm = () => ({
    esi_number: "",
    deduction_cycle: "monthly",
    employeeContribution: 0.75, // Default for form in edit mode
    employerContribution: 3.25, // Default for form in edit mode
    include_employer_contribution: false,
});

export const mapEpfFromServer = (epf) => ({
    epf_number: epf.epf_number || "",
    deduction_cycle: epf.deduction_cycle || "monthly",
    employee_contribution_rate: epf.employee_contribution_rate || "12_actual",
    employer_contribution_rate: epf.employer_contribution_rate || "12_actual",
    include_employer_contribution: epf.include_employer_contribution ?? true,
    include_edli: epf.include_edli ?? false,
    include_admin_charges: epf.include_admin_charges ?? false,
    override_pf: epf.override_pf ?? false,
    pro_rate_pf: epf.pro_rate_pf ?? false,
    consider_applicable_if_less_than_15000:
        epf.consider_applicable_if_less_than_15000 ?? true,
});

export const mapEsiFromServer = (esi) => ({
    esi_number: esi.esi_number || "",
    deduction_cycle: esi.deduction_cycle || "monthly",
    employeeContribution: esi.employeeContribution ?? 0.75, // Use actual value or default
    employerContribution: esi.employerContribution ?? 3.25, // Use actual value or default
    include_employer_contribution: esi.include_employer_contribution ?? false,
});

export const formatEpfNumber = (input) => {
    // Logic remains the same
    let raw = input.toUpperCase().replace(/[^A-Z0-9]/g, "");
    let result = "";
    if (raw.length > 0) result += raw[0].replace(/[^A-Z]/g, "");
    if (raw.length >= 1) result += "/";
    if (raw.length > 1) result += raw.slice(1, 4).replace(/[^A-Z]/g, "");
    if (raw.length >= 4) result += "/";
    if (raw.length > 4) result += raw.slice(4, 12).replace(/[^0-9]/g, "");
    if (raw.length >= 12) result += "/";
    if (raw.length > 12) result += raw.slice(12, 15).replace(/[^A-Z]/g, "");
    return result;
};

export const formatEsiNumber = (value) => {
    // Logic remains the same
    let raw = value.replace(/\D/g, "");
    let part1 = raw.slice(0, 2);
    let part2 = raw.slice(2, 4);
    let part3 = raw.slice(4, 10);
    let part4 = raw.slice(10, 13);
    let part5 = raw.slice(13, 17);
    let formatted = part1;
    if (raw.length > 2) formatted += "-" + part2;
    if (raw.length > 4) formatted += "-" + part3;
    if (raw.length > 10) formatted += "-" + part4;
    if (raw.length > 13) formatted += "-" + part5;
    return formatted;
};

export const getEmployeeContributionLabel = (value) => {
    switch (value) {
        case "12_actual":
            return "12% of Actual PF Wage";
        case "10_actual":
            return "10% of Actual PF Wage";
        case "12_restricted":
            return "12% of Restricted PF Wage (₹15,000 cap)";
        default:
            return value || "--";
    }
};

export const ensureSlabIds = (slabs) => {
    return (slabs || []).map((slab, index) => {
        const uniqueId =
            slab.id || `${Date.now()}-${index}-${Math.floor(Math.random() * 1000)}`;
        return {
            ...slab,
            id: uniqueId,
        };
    });
};

export const prompts = {
    epf: {
        illustration: <TreePalm className="w-20 h-20"/>,
        title: "Are you registered for EPF?",
        description:
            "Any organisation with 20 or more employees must register for the Employee Provident Fund (EPF) scheme, a retirement benefit plan for all salaried employees.",
    },
    esi: {
        illustration: <HospitalIcon  className="w-20 h-20"/>,
        title: "Are you registered for ESI?",
        description:
            "Any organisation with 10 or more employees must register for the Employee State Insurance (ESI) scheme. It is a multidimensional social security system for workers.",
    },
    tds: {
        illustration: <ScrollText className="w-20 h-20"/>,
        title: "Configure TDS",
        description:
            "Tax Deducted at Source (TDS) is a means of collecting tax on income, dividends or asset sales by requiring the payer to deduct tax due before paying the balance to the payee.",
    },
    professional_tax: {
        illustration: <ClipboardList className="w-20 h-20"/>,
        title: "Configure Professional Tax",
        description:
            "Professional Tax is a tax levied by the state government on all types of professions, trades and employment. The tax amount varies by state. Add work location(s) to configure professional tax for your organisation.",
    },
    lwf: {
        illustration: <User/>,
        title: "Configure Labour Welfare Fund",
        description:
            "Labour Welfare Fund is a statutory contribution for employee welfare activities. The contribution varies by state and is paid by both employer and employee.",
    },
    statutory_bonus: {
        illustration: <DollarSign className="w-20 h-20"/>,
        title: "Are your employees eligible to receive statutory bonus?",
        description:
            "According to the Payment of Bonus Act, 1965, an eligible employee can receive a statutory bonus of 8.33% (min) to 20% (max) of their salary earned during a financial year. Configure statutory bonus of your organisation and start paying your employees.",
    },
};